import { useEffect, useState } from "react";
import { Confetti, type ConfettiTier } from "@/components/sensory/Confetti";
import { playToneIfEnabled } from "@/hooks/useSoundscape";

interface SuccessBloomProps {
  /** Pass an incrementing key (e.g. Date.now()) to fire a bloom. Null = idle. */
  triggerKey: number | null;
  hue?: "primary" | "success" | "cyan";
  /** Tier 1 = inline bloom only (default). Tier 2/3 = adds confetti + chime. */
  tier?: 1 | 2 | 3;
}

const HUES = {
  primary: "hsl(var(--primary))",
  success: "hsl(var(--success))",
  cyan: "hsl(var(--primary-glow))",
} as const;

/**
 * Imperative success bloom: a fast radial pulse that overlays the parent.
 * Parent must be `position: relative`. Reuses EventBurst-style palette.
 * Optional tier prop escalates to confetti + chime.
 */
export function SuccessBloom({ triggerKey, hue = "success", tier = 1 }: SuccessBloomProps) {
  const [active, setActive] = useState(false);
  const [confettiKey, setConfettiKey] = useState<number | null>(null);
  const color = HUES[hue];

  useEffect(() => {
    if (!triggerKey) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    setActive(true);
    if (tier >= 2) {
      setConfettiKey(triggerKey);
      playToneIfEnabled("chime");
    }
    const t = setTimeout(() => setActive(false), 700);
    return () => clearTimeout(t);
  }, [triggerKey, tier]);

  return (
    <>
      {active && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit]"
          style={{
            background: `radial-gradient(circle at 50% 50%, ${color} 0%, transparent 60%)`,
            opacity: 0,
            animation: "success-bloom 700ms cubic-bezier(0.22,1,0.36,1) forwards",
            zIndex: 1,
          }}
        />
      )}
      {tier >= 2 && <Confetti triggerKey={confettiKey} tier={(tier as ConfettiTier) >= 3 ? 3 : 2} />}
    </>
  );
}
